/**
 * Theme color resolution shared by the Ui* components.
 *
 * `color` props accept either a theme name exposed by `src/app.css` `@theme`
 * (primary, warning, coldGray, ...) — resolved to its `--color-*` custom
 * property — or any raw CSS color (hex, rgb(), hsl(), named color, ...).
 * Returns `undefined` when unset, so callers can fall back to Tailwind classes.
 */

/** Theme names exposed by `src/app.css` `@theme`, resolvable via `--color-*`. */
const THEME_KEYS: Record<string, true | undefined> = {
  primary: true,
  secondary: true,
  accent: true,
  error: true,
  info: true,
  success: true,
  warning: true,
  coldGray: true,
  lightBlue: true,
  teal: true,
  grey: true,
}

/**
 * Vuetify 2 modifier tokens still present in migrated call-sites
 * (e.g. color="teal lighten-4" on AttendeeList's private icon, "grey lighten-1"
 * on staticCfgLegacy). The captured level digit drives a white/black mix —
 * Vuetify's material palette stepped ~10% per lighten/darken level.
 */
const VUETIFY_MODIFIER_RE =
  /^(primary|secondary|accent|error|info|success|warning|coldGray|lightBlue|teal|grey)\s+(lighten|darken)-([1-4])$/

/** Accepted `color` prop value: a theme name or any CSS color. */
export type ThemeColor = string | undefined

/**
 * Resolve a `color` prop to a CSS color value. Theme names map to their
 * `--color-*` custom property; `#`/`rgb`/`hsl` prefixes and named colors pass
 * through unchanged. Returns `undefined` when `color` is unset or empty.
 */
export function resolveThemeColor(color: ThemeColor): string | undefined {
  if (color == null || color === '') return undefined
  if (
    color.startsWith('#') ||
    color.startsWith('rgb') ||
    color.startsWith('hsl')
  ) {
    return color
  }
  if (THEME_KEYS[color]) return `var(--color-${color})`

  // Vuetify 2 modifier tokens ("teal lighten-4", "grey darken-1", ...) —
  // resolve against the base theme color so the literal string never
  // reaches a CSS color property (where the browser silently drops it and
  // the icon renders with inherited color).
  const modifier = VUETIFY_MODIFIER_RE.exec(color)
  if (modifier != null) {
    const [, name, kind, levelStr] = modifier
    const base = `var(--color-${name})`
    return kind === 'lighten'
      ? `color-mix(in srgb, ${base}, white ${Number(levelStr) * 10}%)`
      : `color-mix(in srgb, ${base}, black ${Number(levelStr) * 10}%)`
  }
  return color
}
